import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import api from '../../../api/client';

export default function VariancePage() {
  const [models,   setModels]   = useState([]);
  const [versions, setVersions] = useState([]);
  const [modelId,  setModelId]  = useState('');
  const [versionId,setVersionId]= useState('');
  const [period,   setPeriod]   = useState('');
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    api.get('/planning/models').then(({ data }) => setModels(data.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!modelId) { setVersions([]); return; }
    api.get(`/planning/versions?model_id=${modelId}`)
      .then(({ data }) => setVersions(data.data?.items || []))
      .catch(() => {});
  }, [modelId]);

  async function handleLoad() {
    if (!versionId || !period) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/planning/variance?version_id=${versionId}&period=${period}`);
      setResult(data.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const chartData = result?.rows?.map((r) => ({
    name:     r.name,
    Plan:     Math.round(r.plan_cents     / 100),
    Actual:   Math.round(r.actual_cents   / 100),
    Variance: Math.round(r.variance_cents / 100),
  })) || [];

  return (
    <div data-testid="planning-variance-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Variance Report — Plan vs Actuals</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          data-testid="planning-variance-model-select"
          value={modelId}
          onChange={(e) => { setModelId(e.target.value); setVersionId(''); setResult(null); }}
        >
          <option value="">— select model —</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select
          data-testid="planning-variance-version-select"
          value={versionId}
          onChange={(e) => { setVersionId(e.target.value); setResult(null); }}
          disabled={!modelId}
        >
          <option value="">— select version —</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.scenario})</option>)}
        </select>

        <input
          data-testid="planning-variance-period-input"
          type="month"
          value={period}
          onChange={(e) => { setPeriod(e.target.value); setResult(null); }}
        />

        <button
          data-testid="planning-variance-load"
          onClick={handleLoad}
          disabled={!versionId || !period || loading}
        >
          {loading ? 'Loading…' : 'Load Variance'}
        </button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {result && (
        <>
          {chartData.length > 0 && (
            <div style={{ width: '100%', height: 300, marginBottom: '1.5rem' }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine y={0} stroke="#666" />
                  <Bar dataKey="Plan"     fill="#4a90d9" />
                  <Bar dataKey="Actual"   fill="#50c878" />
                  <Bar dataKey="Variance" fill="#e87040" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Line</th>
                <th style={{ textAlign: 'right' }}>Plan (¢)</th>
                <th style={{ textAlign: 'right' }}>Actual (¢)</th>
                <th style={{ textAlign: 'right' }}>Variance (¢)</th>
                <th style={{ textAlign: 'right' }}>Δ%</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.lineId} data-testid={`planning-variance-row-${r.lineId}`}>
                  <td>{r.name}</td>
                  <td style={{ textAlign: 'right' }}>{r.plan_cents.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{r.actual_cents.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', color: r.variance_cents < 0 ? 'red' : 'green' }}>
                    {r.variance_cents >= 0 ? '+' : ''}{r.variance_cents.toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.variance_pct != null ? `${r.variance_pct}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #ccc' }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{result.totals.plan_cents.toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>{result.totals.actual_cents.toLocaleString()}</td>
                <td style={{ textAlign: 'right', color: result.totals.variance_cents < 0 ? 'red' : 'green' }}>
                  {result.totals.variance_cents >= 0 ? '+' : ''}{result.totals.variance_cents.toLocaleString()}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </>
      )}

      {result && !result.rows.length && <p>No plan lines for this period.</p>}
    </div>
  );
}
